import React, { useState, useEffect } from 'react';
import { CommonLayout } from '../../commonComponents';
import {
    Container,
    Typography,
    Grid,
    TextField,
    Button,
    useTheme,
    useMediaQuery,
    Box,
    CardMedia,
    Alert,
    CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { GetRiskDetailsResponse } from '../../../redux-store/types/api-types';
import { GetRiskDetails, SendLink } from '../../../redux-store/actions/userManagement';
import { RootStateType } from '../../../redux-store/reducers';

const RiskAssessmentProfile = () => {
    const [pan, setPan] = useState('');
    const [mobile, setMobile] = useState('');
    const [email, setEmail] = useState('');
    const [riskAssessmentStatus, setRiskAssessmentStatus] = useState<number | null>(null);
    const root = useSelector((state: RootStateType) => state);
    console.log(root)
    console.log(root.auth.userDetails.emailID)
    console.log(root.auth.userDetails.Header_ID)
    const [panError, setPanError] = useState('');
    const [mobileError, setMobileError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();

    const handlePanChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        setPan(value);

        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
            setPanError('Invalid PAN format');
            return;
        } else {
            setPanError('');
        }

        if (value.length === 10) {
            try {
                const response = await dispatch(GetRiskDetails({ pan: value })) as unknown as GetRiskDetailsResponse;
                const result = response?.[0];
                if (result) {
                    setRiskAssessmentStatus(parseInt(result.RiskAssessmentStatus));

                    setMobile(result.Mobile !== 'NA' ? result.Mobile : '');
                    setEmail(result.EmailID !== 'NA' ? result.EmailID : '');
                }
            } catch (err) {
                console.error('Error fetching risk details:', err);
            }
        }
    };

    const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setMobile(value);
        setMobileError(/^\d{10}$/.test(value) ? '' : 'Must be a 10-digit number');
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);
        setEmailError(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Invalid email address');
    };

    // ADDED: Helper function to convert a string to an ArrayBuffer
    const str2ab = (str: string) => {
        const buf = new ArrayBuffer(str.length * 2); // 2 bytes for each char
        const bufView = new Uint16Array(buf);
        for (let i = 0, strLen = str.length; i < strLen; i++) {
            bufView[i] = str.charCodeAt(i);
        }
        return buf;
    };

    // ADDED: Helper function to get an importable cryptographic key
    const getCryptoKey = async (secret: string) => {
        const encoder = new TextEncoder();
        const keyData = encoder.encode(secret); // Correctly creates a 32-byte (256-bit) Uint8Array

        return await window.crypto.subtle.importKey(
            'raw',
            keyData,
            { name: 'AES-GCM', length: 256 },
            true,
            ['encrypt', 'decrypt']
        );
    };

    const toBase64Url = (data: Uint8Array): string => {
        const base64 = btoa(String.fromCharCode.apply(null, Array.from(data)));
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); // Replace special chars and remove padding
    };

    const handleSendLink = async () => {

        if (panError || mobileError || emailError || !pan || !mobile || !email) {
            alert('Please fix the errors and fill all fields before sending.');
            return;
        }

        if (isSending) {
            return;
        }

        setLoading(true);
        setIsSending(true);

        const secretKey = 'A?D(G+KbPeShVmYq3t6w9z$C&F)J@NcQ';
        if (!secretKey) {
            console.error('Encryption key is not defined.');
            alert('Could not send the link due to a configuration error.');
            setLoading(false);
            setIsSending(false);
            return;
        }

        try {
            const dataToEncrypt = {
                mobile: mobile,
                email: email,
                rmID: root?.auth?.userDetails?.Header_ID,
                rmMail: root?.auth?.userDetails.emailID,
                pan: pan
            };
            const jsonString = JSON.stringify(dataToEncrypt);
            const dataEncoder = new TextEncoder();

            const cryptoKey = await getCryptoKey(secretKey);
            const iv = window.crypto.getRandomValues(new Uint8Array(12));

            const encryptedDataBuffer = await window.crypto.subtle.encrypt(
                { name: 'AES-GCM', iv: iv },
                cryptoKey,
                dataEncoder.encode(jsonString)
            );

            const combinedBuffer = new Uint8Array(iv.length + encryptedDataBuffer.byteLength);
            combinedBuffer.set(iv, 0);
            combinedBuffer.set(new Uint8Array(encryptedDataBuffer), iv.length);

            const urlSafeEncryptedData = toBase64Url(combinedBuffer);

            const baseUrl = window.location.origin;
            const finalUrl = `${baseUrl}/assessment/investor-login/${urlSafeEncryptedData}`;

            console.log('Generated Link:', finalUrl);

            const payload = {
                emailId: email,
                rmEmailId: root?.auth?.userDetails.emailID,
                mobileNumber: Number(mobile),
                link: finalUrl,
            };

            const apiResponse = await dispatch(SendLink(payload)) as unknown as any;

            if (apiResponse && apiResponse.Status) {
                alert(`Status: ${apiResponse.Status}`);
            }

        } catch (error) {
            console.error('Process failed:', error);
            alert('Could not generate/send the link due to an error.');
        } finally {
            setLoading(false);
            setIsSending(false);
        }
    };

    return (
        <CommonLayout>
            <Container maxWidth="lg" sx={{ mt: 5 }}>
                <Grid container spacing={4} alignItems="center">
                    {!isMobile && (
                        <Grid item xs={12} md={6}>
                            <CardMedia
                                component="img"
                                src="/images/form-risk.png"
                                alt="Illustration"
                                sx={{ width: '115%', height: 'auto', marginTop: 2, marginLeft: -4 }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <Box sx={{ maxWidth: 500, mx: 'auto', marginTop: 2 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    fontWeight: 'bold',
                                    color: 'primary.main',
                                    mb: 3,
                                    textAlign: 'left',
                                }}
                            >
                                Send Risk Assessment Link
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Enter Investor PAN"
                                        value={pan}
                                        onChange={handlePanChange}
                                        variant="outlined"
                                        inputProps={{
                                            maxLength: 10,
                                            style: { textTransform: 'uppercase' },
                                        }}
                                        error={!!panError}
                                        helperText={panError}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Mobile Number"
                                        value={mobile}
                                        onChange={handleMobileChange}
                                        variant="outlined"
                                        type="tel"
                                        inputProps={{ maxLength: 10 }}
                                        error={!!mobileError}
                                        helperText={mobileError}
                                        disabled={riskAssessmentStatus !== null && riskAssessmentStatus > 0}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Email Address"
                                        value={email}
                                        onChange={handleEmailChange}
                                        variant="outlined"
                                        type="email"
                                        error={!!emailError}
                                        helperText={emailError}
                                        disabled={riskAssessmentStatus !== null && riskAssessmentStatus > 0}
                                    />
                                </Grid>

                                {riskAssessmentStatus !== null && riskAssessmentStatus > 0 && (
                                    <Grid item xs={12}>
                                        <Alert severity="info">
                                            Risk assessment already completed.
                                        </Alert>
                                    </Grid>
                                )}

                                {riskAssessmentStatus === 0 && (
                                    <Grid item xs={12}>
                                        <Grid container justifyContent="center">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={handleSendLink}
                                                disabled={loading}
                                                size="large"
                                                sx={{
                                                    minWidth: '200px',
                                                    py: 1.5,
                                                    textTransform: 'none',
                                                    fontSize: '1rem',
                                                    height: '2.5rem'
                                                }}
                                            >
                                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Link to Investor'}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </CommonLayout>
    );
};

export default RiskAssessmentProfile;
